// TODO: Remove Try Catch from all API functions because its handled in functions that call it

import { config } from "../config";
import {
  TBeforeAfterProcessing,
  TConversation,
  TMessageSender,
  TSession,
  TTemplate,
  TClinicPatientType,
  TSurgeryStageType,
} from "../utils/types";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import { pollData } from "../utils/functions";
import { buildClinicGallerySearchParams } from "./clinicGalleryQuery";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const urlPrefix = `${protocol}://${host}/clinics/v1/clinic`;
const urlPrefixSideBySide = `${protocol}://${host}/clinics/v1/side-by-side`;
const urlPrefixUser = `${protocol}://${host}/clinics/v1/user`;
const urlPrefixMgmtClinic = `${protocol}://${host}/clinics/v1/mgmt-clinic/clinics`;
const clinicosId = "0471449b-020c-4074-a2a1-c92a9139e09f";

export type TGetSimulationResponse = {
  simulations: {
    id: number;
    clinic_id: number;
  }[];
};

export type TTransform = {
  id: number;
  patient_id: number | null;
  patient_name: string | null;
  batch_id: number | null;
  download_url: string;
  download_urls?: string[];
  procedure_types?: string[];
  metadata?: {
    procedure_types?: string[];
    [key: string]: unknown;
  };
  image_data?: {
    procedure_types?: string[];
    [key: string]: unknown;
  };
  upload_type?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

export type TGetTransformsResponse = {
  results: TTransform[];
  limit: number;
  has_more: boolean;
  next_cursor: string | null;
};

export const getTransforms = async (
  session: TSession,
  options: {
    sources?: string[];
    limit?: number;
    cursor?: string | null;
  } = {},
): Promise<TGetTransformsResponse> => {
  if (!session?.clinicId) {
    throw new Error("Missing clinicId for transforms fetch");
  }

  const sources = options.sources?.length ? options.sources : ["dashboard"];
  const limit = Math.min(Math.max(options.limit ?? 100, 1), 200);

  const params = new URLSearchParams({ limit: String(limit) });
  sources.forEach((source) => params.append("source", source));
  if (options.cursor) params.append("cursor", options.cursor);
  const response = await fetchWithAuthRetry(
    `${urlPrefix}/${session.clinicId}/transforms?${params.toString()}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  const data = await response.json();
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    limit: data?.limit ?? limit,
    has_more: Boolean(data?.has_more),
    next_cursor:
      typeof data?.next_cursor === "string" ? data.next_cursor : null,
  };
};

export const getSimulations = async (
  session: TSession,
  start_date: Date,
  end_date: Date,
): Promise<TGetSimulationResponse> => {
  try {
    const formattedStartDate =
      start_date.toISOString().split("T")[0] + "T00:00:00.000000";
    const formattedEndDate =
      end_date.toISOString().split("T")[0] + "T23:59:59.000000";

    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/simulations?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
      {
        method: "GET",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting simulations:", error);
    throw error;
  }
};

export type TGetClinicInfoResponse = {
  clinic_name: string;
  logo_url: string;
  logo_url_rectangle: string;
  primary_goal?: string | null;
  website_url?: string | null;
  widget_deployed?: boolean;
  widget_deployed_at?: string | null;
  widget_deployed_checked_at?: string | null;
  widget_deployed_form_name?: string | null;
  widget_deployed_origin_host?: string | null;
  widget_deployed_referer_host?: string | null;
  free_trial_end_date?: string | null;
  surgeons: {
    name: string;
    photo_url: string;
    address: string;
  }[];
  procedures: any[];
  invite_code: string;
};
export const getClinicInfo = async (
  accessToken: string,
  clinicId: string,
  options: { signal?: AbortSignal } = {},
): Promise<TGetClinicInfoResponse> => {
  try {
    const response = await fetchWithAuthRetry(`${urlPrefix}/${clinicId}/info`, {
      method: "GET",
      signal: options.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error getting clinic info", error);
    throw error;
  }
};

export type TLeadCaptureLinkResponse = {
  slug: string;
  url: string;
};

const getApiErrorMessage = async (
  response: Response,
  fallbackMessage: string,
) => {
  const body = await response
    .clone()
    .json()
    .catch(() => null);

  if (typeof body?.message === "string") return body.message;
  if (typeof body?.detail === "string") return body.detail;
  if (typeof body?.error === "string") return body.error;

  return fallbackMessage;
};

export const getLeadCaptureLink = async (
  session: TSession,
  options: { signal?: AbortSignal } = {},
): Promise<TLeadCaptureLinkResponse> => {
  if (!session?.clinicId) {
    throw new Error("Missing clinic ID for lead capture link.");
  }

  const response = await fetchWithAuthRetry(
    `${urlPrefix}/${session.clinicId}/lead-capture-link`,
    {
      method: "GET",
      signal: options.signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      await getApiErrorMessage(response, "Lead capture link is unavailable."),
    );
  }

  return await response.json();
};

export const updateLeadCaptureLink = async (
  session: TSession,
  slug: string,
): Promise<TLeadCaptureLinkResponse> => {
  if (!session?.clinicId) {
    throw new Error("Missing clinic ID for lead capture link.");
  }

  const response = await fetchWithAuthRetry(
    `${urlPrefix}/${session.clinicId}/lead-capture-link`,
    {
      method: "PATCH",
      body: JSON.stringify({ slug }),
    },
  );

  if (!response.ok) {
    throw new Error(
      await getApiErrorMessage(response, "Unable to save this custom slug."),
    );
  }

  return await response.json();
};

export type TPostClinicPhotoRequest = {
  imageBlob: Blob;
};
export type TPostClinicPhotoResponse = {
  photo_url: string;
};
export const postClinicPhoto = async (
  session: TSession,
  body: TPostClinicPhotoRequest,
): Promise<TPostClinicPhotoResponse> => {
  try {
    // const clinicPhotoRes = await fetchWithAuthRetry(
    //   `${urlPrefix}/${session.clinicId}/clinic-logo`,
    //   {
    //     method: "POST",
    //   },
    // );
    const clinicPhotoRes = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/clinic-logo`,
      {
        method: "POST",
      },
    );

    if (!clinicPhotoRes.ok) {
      throw new Error(`HTTP error ${clinicPhotoRes.status}`);
    }

    const clinicPhotoJson = await clinicPhotoRes.json();

    const putRes = await fetch(clinicPhotoJson.upload_url, {
      method: "PUT",
      headers: {
        "Content-Type": "image/png",
      },
      body: body.imageBlob,
    });

    if (!putRes.ok) {
      const text = await putRes.text();
      throw new Error(`HTTP Error: ${putRes.status} - ${text}`);
    }

    const result: TPostClinicPhotoResponse = {
      photo_url: `https://www.clinic-logo-assets.clinic-os.com/${clinicPhotoJson.file_name}`,
    };
    return result;
  } catch (error) {
    console.error("Error posting clinic photo", error);
    throw error;
  }
};

export type TPostClinicProfileRequest = {
  surgeon_info?: {
    name: string;
    clinic_address: string;
    photo_url: string;
  };
  logo_url?: string;
  logo_url_rectangle?: string;
  primary_goal?: string;
  website_url?: string;
  emails?: string[];
  widget_settings?: object;
};
export type TPostClinicProfileResponse = {
  surgeon_info?: {
    name: string;
    clinic_address: string;
    photo_url: string;
  };
  logo_url?: string;
  logo_url_rectangle?: string;
  primary_goal?: string;
  website_url?: string;
  emails?: string[];
  widget_settings?: object;
};
export const postClinicProfile = async (
  session: TSession,
  body: TPostClinicProfileRequest,
): Promise<TPostClinicProfileResponse> => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/profile`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    const result: TPostClinicProfileResponse = {
      ...(data?.surgeon_info
        ? {
            surgeon_info: {
              name: data.surgeon_info.name,
              clinic_address: data.surgeon_info.clinic_address,
              photo_url: data.surgeon_info.photo_url,
            },
          }
        : {}),
      logo_url: data?.logo_url,
      logo_url_rectangle: data?.logo_url_rectangle,
      primary_goal: data?.primary_goal,
      website_url: data?.website_url,
      emails: data?.emails,
      widget_settings: data?.widget_settings,
    };
    return result;
  } catch (error) {
    console.error("Error getting clinic info", error);
    throw error;
  }
};

export type TPatientInfo = {
  areasOfInterest: string[];
  createdAt: string;
  dateOfBirth: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  proceduresOfInterest: string[];
  sex: string;
  updatedAt: string;
  uuid: string;
  uploadType: string;
  location: string;
  type: string;
};

export type TGetPatientsResponse = TPatientInfo[];

export type TPatientsPagination = {
  limit: number;
  offset: number;
  hasMore: boolean;
  nextCursor: string | null;
};

export type TGetPatientsPaginatedResponse = {
  patients: TGetPatientsResponse;
  pagination: TPatientsPagination | null;
};

export type TGetPatientsOptions = {
  patientTypes?: TClinicPatientType[];
  search?: string;
  email?: string;
  phone?: string;
  name?: string;
  useSubstring?: boolean;
  externalIds?: string[];
  patientIds?: string[];
  countImages?: boolean;
  use_dummy_data?: boolean;
  limit?: number;
  offset?: number;
  cursor?: string;
};

export type TClinicPatientImage = {
  image_data: {
    id: number;
    procedure_types: string[];
    procedure_types_human_readable: string[];
    view_type: string | null;
    view_type_human_readable: string | null;
    upload_type: string;
    uploaded: boolean;
    days_postop: number | null;
    surgery_stage: string | null;
    taken_in_clinic: boolean | null;
    is_starred: boolean;
    external_id: string | null;
    tags: string[];
    created_at: string;
    updated_at: string;
    date_taken?: string | null;
  };
  presigned_urls: string[];
  thumbnail_presigned_urls: string[];
};

export const getPatientImages = async (
  session: TSession,
  patientId: string,
  options?: {
    isStarred?: boolean;
    uploadTypes?: string[];
    isInBatch?: boolean;
    viewTypes?: string[];
  },
): Promise<TClinicPatientImage[]> => {
  const queryParams = new URLSearchParams();
  if (options?.isStarred !== undefined) {
    queryParams.append("is_starred", String(options.isStarred));
  }
  if (options?.uploadTypes) {
    options.uploadTypes.forEach((type) =>
      queryParams.append("upload_types", type),
    );
  }
  if (options?.isInBatch !== undefined) {
    queryParams.append("is_in_batch", String(options.isInBatch));
  }
  if (options?.viewTypes) {
    options.viewTypes.forEach((viewType) =>
      queryParams.append("view_types", viewType),
    );
  }

  const qs = queryParams.toString();
  const response = await fetchWithAuthRetry(
    `${urlPrefix}/${session.clinicId}/patients/${patientId}/images${
      qs ? `?${qs}` : ""
    }`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  const data = await response.json();
  return Array.isArray(data?.images)
    ? (data.images as TClinicPatientImage[])
    : [];
};

export type TUpdatePatientImageRequest = {
  surgery_stage?: TSurgeryStageType;
};

export const patchPatientImage = async (
  session: TSession,
  patientId: string,
  imageId: number | string,
  body: TUpdatePatientImageRequest,
): Promise<TClinicPatientImage["image_data"]> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefix}/${session.clinicId}/patients/${patientId}/images/${imageId}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  const data = await response.json().catch(() => null);
  return data?.image_data ?? data;
};

export type TReprocessClinicImagesRequest = {
  patient_uuid?: string;
  force?: boolean;
  upcoming_appointments_only?: boolean;
  appointment_start_date?: string;
  appointment_end_date?: string;
  limit?: number;
};

export type TReprocessClinicImagesResponse = {
  num_images: number;
  metadata_url: string | null;
};

export const reprocessClinicImages = async (
  session: TSession,
  body: TReprocessClinicImagesRequest = {},
): Promise<TReprocessClinicImagesResponse> => {
  const clinicId = session?.clinicId;
  if (!clinicId) {
    throw new Error("Missing clinic ID for image reprocessing.");
  }

  const payload: TReprocessClinicImagesRequest = {};
  if (body.patient_uuid) payload.patient_uuid = body.patient_uuid;
  if (body.force !== undefined) payload.force = body.force;
  if (body.upcoming_appointments_only !== undefined) {
    payload.upcoming_appointments_only = body.upcoming_appointments_only;
  }
  if (body.appointment_start_date) {
    payload.appointment_start_date = body.appointment_start_date;
  }
  if (body.appointment_end_date) {
    payload.appointment_end_date = body.appointment_end_date;
  }
  if (body.limit !== undefined) payload.limit = body.limit;

  const response = await fetchWithAuthRetry(
    `${urlPrefixMgmtClinic}/${clinicId}/image/reprocess`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  const errorBody = await response
    .clone()
    .json()
    .catch(() => null);

  if (response.status === 202) {
    const data = await response.json();
    return {
      num_images: Number(data?.num_images ?? 0),
      metadata_url: data?.metadata_url ?? null,
    };
  }

  if (response.status === 400) {
    if (errorBody?.error_code === "CLINICUSER__INVALID_RELATIONSHIP") {
      throw new Error("You are not a member of this clinic.");
    }
    throw new Error(errorBody?.message ?? "Invalid reprocess request.");
  }

  if (response.status === 403) {
    throw new Error("Missing or invalid authentication.");
  }

  throw new Error(
    errorBody?.message ??
      `Failed to start thumbnail reprocessing (HTTP ${response.status}).`,
  );
};

export const getMultiplePatientsImages = async (
  session: TSession,
  patientIds: string[],
  options?: {
    isStarred?: boolean;
    uploadTypes?: string[];
    isInBatch?: boolean;
    viewTypes?: string[];
  },
): Promise<Record<string, TClinicPatientImage[]>> => {
  if (!patientIds.length) {
    return {};
  }

  const queryParams = new URLSearchParams();
  patientIds.forEach((id) => queryParams.append("patient_ids", id));

  if (options?.isStarred !== undefined) {
    queryParams.append("is_starred", String(options.isStarred));
  }
  if (options?.uploadTypes) {
    options.uploadTypes.forEach((type) =>
      queryParams.append("upload_types", type),
    );
  }
  if (options?.isInBatch !== undefined) {
    queryParams.append("is_in_batch", String(options.isInBatch));
  }
  if (options?.viewTypes) {
    options.viewTypes.forEach((viewType) =>
      queryParams.append("view_types", viewType),
    );
  }

  const qs = queryParams.toString();
  const response = await fetchWithAuthRetry(
    `${urlPrefix}/${session.clinicId}/images${qs ? `?${qs}` : ""}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  const data = await response.json();
  return data?.images_by_patient || {};
};

// Overloads: old signature OR new options object
export function getPatientsPaginated(
  session: TSession,
  patientTypes?: TClinicPatientType[],
  email?: string,
  use_dummy_data?: boolean,
): Promise<TGetPatientsPaginatedResponse>;
export function getPatientsPaginated(
  session: TSession,
  options?: TGetPatientsOptions,
): Promise<TGetPatientsPaginatedResponse>;

export async function getPatientsPaginated(
  session: TSession,
  a?: any,
  b?: any,
  c?: any,
): Promise<TGetPatientsPaginatedResponse> {
  // Normalize params to an options object
  let opts: TGetPatientsOptions = {};
  if (Array.isArray(a) || typeof b === "string" || typeof c === "boolean") {
    // old signature: (session, patientTypes?, email?, use_dummy_data?)
    opts = {
      patientTypes: a as TClinicPatientType[] | undefined,
      email: b as string | undefined,
      use_dummy_data: c as boolean | undefined,
    };
  } else {
    opts = (a || {}) as TGetPatientsOptions; // new signature
  }

  // Build query params
  const qp = new URLSearchParams();
  opts.patientTypes?.forEach((t) => qp.append("type", t));
  if (opts.search) qp.append("search", opts.search);
  if (opts.email) qp.append("email", opts.email);
  if (opts.phone) qp.append("phone", opts.phone);
  if (opts.name) qp.append("name", opts.name);
  if (opts.useSubstring) qp.append("use_substring", "true");
  opts.externalIds?.forEach((id) => qp.append("external_ids", id));
  opts.patientIds?.forEach((id) => qp.append("patient_ids", id));
  if (opts.countImages) qp.append("count_images", "true");
  if (opts.use_dummy_data) qp.append("use_dummy_data", "true");
  if (opts.limit !== undefined) qp.append("limit", String(opts.limit));
  if (opts.offset !== undefined) qp.append("offset", String(opts.offset));
  if (opts.cursor !== undefined) qp.append("cursor", opts.cursor);

  const qs = qp.toString();
  const url = `${urlPrefix}/${session.clinicId}/patients${qs ? `?${qs}` : ""}`;

  const resp = await fetchWithAuthRetry(url, { method: "GET" });
  if (!resp.ok) throw new Error(`HTTP error ${resp.status}`);

  const data = await resp.json();
  const patients: TGetPatientsResponse = (data.patients || []).map(
    (item: any) => ({
      uploadType: item.upload_type || "",
      location: item.location || "",
      areasOfInterest: item.areas_of_interest || [],
      createdAt: item.created_at || "",
      dateOfBirth: item.dob || "",
      email: item.email || "",
      firstName: item.first_name || "",
      lastName: item.last_name || "",
      phone: item.phone || "+1",
      proceduresOfInterest: item.procedures_of_interest || [],
      sex: item.sex || "",
      updatedAt: item.updated_at || "",
      uuid: item.uuid || "",
      type: item.type || "",
    }),
  );

  const pagination: TPatientsPagination | null = data?.pagination
    ? {
        limit: data.pagination.limit ?? patients.length,
        offset: data.pagination.offset ?? 0,
        hasMore: Boolean(data.pagination.has_more),
        nextCursor: data.pagination.next_cursor ?? null,
      }
    : null;

  return { patients, pagination };
}

// Single impl for backward-compatible list-only calls
export function getPatients(
  session: TSession,
  patientTypes?: TClinicPatientType[],
  email?: string,
  use_dummy_data?: boolean,
): Promise<TGetPatientsResponse>;
export function getPatients(
  session: TSession,
  options?: TGetPatientsOptions,
): Promise<TGetPatientsResponse>;
export async function getPatients(
  session: TSession,
  a?: any,
  b?: any,
  c?: any,
): Promise<TGetPatientsResponse> {
  const response = await getPatientsPaginated(session, a, b, c);
  return response.patients;
}

export type TClinicGalleryMode = "chronological" | "patient_alpha";

export type TClinicGalleryPatientInfo = {
  uuid?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  email?: string;
  phone?: string;
  dob?: string;
  created_at?: string;
  updated_at?: string;
  sex?: string;
  areas_of_interest?: string[];
  procedures_of_interest?: string[];
  location?: string;
  upload_type?: string;
  type?: string;
};

export type TClinicGalleryItem = {
  item_key: string;
  image_id: string | number | null;
  type: "regular" | "before_after";
  created_at: string;
  display_date: string;
  patient_uuid: string;
  patient_id: number | null;
  patient_name: {
    first_name: string;
    last_name: string;
    display_name: string;
  };
  patient_email?: string | null;
  patient_phone?: string | null;
  thumbnail_url: string | null;
  full_size_url?: string | null;
  asset_id: string | null;
  grouping_keys: Record<string, string>;
  before_after_batch_uuid?: string | null;
  patient_info?: TClinicGalleryPatientInfo | null;
  upload_type: string;
};

export type TClinicGalleryGroup = {
  group_label: string;
  group_type: string;
  item_ids: string[];
  children: TClinicGalleryGroup[];
};

export type TClinicGalleryResponse = {
  mode: TClinicGalleryMode;
  limit: number;
  timezone: string;
  items: TClinicGalleryItem[];
  groups: TClinicGalleryGroup[];
  next_cursor: string | null;
};

export type TClinicGalleryRequest = {
  mode?: TClinicGalleryMode;
  limit?: number;
  cursor?: string | null;
  timezone?: string;
  include_patient_info?: boolean;
  uploadTypes?: string[];
  signal?: AbortSignal;
};

export const getClinicGallery = async (
  session: TSession,
  params: TClinicGalleryRequest = {},
): Promise<TClinicGalleryResponse> => {
  const qp = buildClinicGallerySearchParams({
    mode: params.mode,
    limit: params.limit,
    cursor: params.cursor,
    timezone: params.timezone,
    includePatientInfo: params.include_patient_info,
    uploadTypes: params.uploadTypes,
  });

  const qs = qp.toString();
  const url = `${urlPrefix}/${session.clinicId}/gallery${qs ? `?${qs}` : ""}`;

  const resp = await fetchWithAuthRetry(url, {
    method: "GET",
    signal: params.signal,
  });

  if (!resp.ok) {
    throw new Error(`HTTP error ${resp.status}`);
  }

  return (await resp.json()) as TClinicGalleryResponse;
};

export type TImageEditTargetType = "clinic_patient_image";
export type TImageEditOperation = {
  type: "rotate";
  degrees: number;
};
export type TCreateImageEditRequest = {
  target_type: TImageEditTargetType;
  target_id: number;
  operations: [TImageEditOperation];
  client_request_id: string;
};
export type TImageEditJobResponse = {
  task_id: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  target_type: TImageEditTargetType;
  target_id: number;
  operations: TImageEditOperation[];
  error_message?: string | null;
  full_size_url?: string | null;
  thumbnail_url?: string | null;
};

export const createImageEdit = async (
  session: TSession,
  request: TCreateImageEditRequest,
): Promise<TImageEditJobResponse> => {
  const resp = await fetchWithAuthRetry(
    `${urlPrefix}/${session.clinicId}/image-edits`,
    {
      method: "POST",
      body: JSON.stringify(request),
    },
  );

  if (!resp.ok) {
    throw new Error(`HTTP error ${resp.status}`);
  }

  return (await resp.json()) as TImageEditJobResponse;
};

export const getImageEditJob = async (
  session: TSession,
  taskId: number,
): Promise<TImageEditJobResponse> => {
  const resp = await fetchWithAuthRetry(
    `${urlPrefix}/${session.clinicId}/image-edits/${taskId}`,
    {
      method: "GET",
    },
  );

  if (!resp.ok) {
    throw new Error(`HTTP error ${resp.status}`);
  }

  return (await resp.json()) as TImageEditJobResponse;
};

export type TPostPatientsResponse = TPatientInfo;
export const postPatients = async (
  session: TSession,
  patientData: any,
): Promise<TPostPatientsResponse> => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/patients`,
      {
        method: "POST",
        body: JSON.stringify(patientData),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    const result: TPostPatientsResponse = {
      uploadType: "Dashboard",
      location: data.location || "",
      areasOfInterest: data.areas_of_interest || [],
      createdAt: data.created_at || "",
      dateOfBirth: data.dob || "",
      email: data.email || "",
      firstName: data.first_name || "",
      lastName: data.last_name || "",
      phone: data.phone || "+1",
      proceduresOfInterest: data.procedures_of_interest || [],
      sex: data.sex || "",
      updatedAt: data.updated_at || "",
      uuid: data.uuid || "",
      type: data.type || "",
    };
    return result;
  } catch (error) {
    console.error("Error adding a patient", error);
    throw error;
  }
};

export type TInputImage = {
  url: string;
  s3_key: string;
  processed_s3_key: string;
  processed_get_url: string;
  label: string;
  thumbnail_s3_key: string;
  id_in_database: number;
};

export type TPostSideBySidePairsUploadResponse = {
  before_input_images: TInputImage[];
  after_input_images: TInputImage[];
  presigned_put_urls_before_images: string[];
  presigned_put_urls_after_images: string[];
  processed_presigned_get_urls_before_images: string[];
  processed_presigned_get_urls_after_images: string[];
  batch_uuid: string;
};
export type TPostSideBySidePairsUploadRequest = {
  num_before_images: number;
  num_after_images: number;
};
export const postSideBySidePairsUpload = async (
  session: TSession,
  body: TPostSideBySidePairsUploadRequest,
  images: Blob[],
  signal: AbortSignal,
): Promise<TPostSideBySidePairsUploadResponse> => {
  const clinicId = session?.clinicId || clinicosId;

  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${clinicId}/before-after/upload`,
    {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  const data = await response.json();

  await Promise.all(
    [
      ...data.presigned_put_urls_before_images,
      ...data.presigned_put_urls_after_images,
    ].map((putUrl: string, index: number) =>
      fetch(putUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "image/png",
        },
        body: images[index],
        signal,
      })
        .then((response) => {
          if (!response.ok) {
            return response.text().then((text) => {
              throw new Error(`HTTP Error: ${response.status} - ${text}`);
            });
          }
        })
        .catch((error) => {
          throw error;
        }),
    ),
  );

  await Promise.all(
    [
      ...data.before_input_images.map((image: TInputImage) => image.url),
      ...data.after_input_images.map((image: TInputImage) => image.url),
    ].map((url) => pollData(url, signal)),
  );

  return data;
};

// NOTE: This function return more than just the all_pairs array
// But we only need some of the data from the all_pairs array
// So we only type some of the data that is needed.
export type TSideBySideProcessedImage = TInputImage & {
  imageDimensions: { width: number; height: number };
  cropCoords: {
    cropX: number;
    cropY: number;
    cropWidth: number;
    cropHeight: number;
  };
  censorship?: TSideBySideCensorship | null;
};

export type TSideBySideCensorship = {
  finalBboxesOnOriginal: Record<
    string,
    [number, number, number, number] | null
  >;
};

export type TSideBySidePair = {
  before: TSideBySideProcessedImage;
  after: TSideBySideProcessedImage;
  concat: {
    put_url: string;
    s3_key: string;
    thumbnail_s3_key: string;
    filename: string;
    id_in_database: number;
  };
};

export type TPostSideBySideUnsortedBatchResponse = {
  batchUuid?: string;
  allPairs: TSideBySidePair[];
  unpairedBeforeImages: TSideBySideProcessedImage[];
  unpairedAfterImages: TSideBySideProcessedImage[];
};

export type TCensorshipAIFilterMode = "off" | "detect" | "apply";

export type TCensorshipAIFilterRequest = {
  mode: TCensorshipAIFilterMode;
  asset_url?: string;
};

export type TAIFiltersRequest = {
  censorship?: TCensorshipAIFilterRequest;
};

export type TPostSideBySideUnsortedBatchRequest = {
  batch_uuid: string;
  before_input_images: TInputImage[];
  after_input_images: TInputImage[];
  predefined_input_images_pairs: {
    before_image: TInputImage;
    after_image: TInputImage;
  }[];
  template: {};
  is_remove_face: boolean;
  is_remove_background: boolean;
  is_cover_tattoo: boolean;
  is_cover_private_parts: boolean;
  is_fix_mode: boolean;
  ai_filters?: TAIFiltersRequest;
};
export const postSideBySideUnsortedBatch = async (
  session: TSession,
  body: TPostSideBySideUnsortedBatchRequest,
  signal: AbortSignal,
): Promise<TPostSideBySideUnsortedBatchResponse> => {
  const clinicId = session?.clinicId || clinicosId;
  // Make the initial API request
  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${clinicId}/before-after/create`,
    {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    },
  );
  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }
  const data = await response.json();
  const metadataResponse = await pollData(data.metadata_url, signal);
  const metadata = await metadataResponse.json();
  if (!metadata.success) throw new Error("Error processing images");

  const toCropCoords = (preprocessedImage: any) => {
    const dimensions = preprocessedImage?.dimensions || [1, 1];
    const width = Math.max(1, dimensions[0] || 1);
    const height = Math.max(1, dimensions[1] || 1);
    const cropCoords = preprocessedImage?.ideal_crop_coords;

    if (!Array.isArray(cropCoords) || cropCoords.length < 4) {
      return {
        cropX: 0,
        cropY: 0,
        cropWidth: width,
        cropHeight: height,
      };
    }

    return {
      cropX: cropCoords[0],
      cropY: cropCoords[1],
      cropWidth: cropCoords[2] - cropCoords[0],
      cropHeight: cropCoords[3] - cropCoords[1],
    };
  };

  const toCensorship = (censorship: any): TSideBySideCensorship => {
    const finalBboxesOnOriginal: Record<
      string,
      [number, number, number, number] | null
    > = {};
    const source = censorship?.final_bboxes_on_original;

    if (source && typeof source === "object") {
      Object.entries(source).forEach(([key, bbox]) => {
        if (
          Array.isArray(bbox) &&
          bbox.length >= 4 &&
          bbox.every((value) => Number.isFinite(Number(value)))
        ) {
          finalBboxesOnOriginal[key] = [
            Number(bbox[0]),
            Number(bbox[1]),
            Number(bbox[2]),
            Number(bbox[3]),
          ];
          return;
        }

        finalBboxesOnOriginal[key] = null;
      });
    }

    return { finalBboxesOnOriginal };
  };

  const toImageDetailsMap = (
    imageDetails: Array<{
      id_in_database: number;
      preprocessed_image: any;
      censorship?: any;
    }>,
  ) =>
    new Map(
      imageDetails.map((item) => {
        const dimensions = item.preprocessed_image?.dimensions || [1, 1];
        const width = Math.max(1, dimensions[0] || 1);
        const height = Math.max(1, dimensions[1] || 1);
        return [
          item.id_in_database,
          {
            imageDimensions: { width, height },
            cropCoords: toCropCoords(item.preprocessed_image),
            censorship: toCensorship(item.censorship),
          },
        ];
      }),
    );

  const beforeImageDetailsMap = toImageDetailsMap(
    metadata.image_details_before || [],
  );
  const afterImageDetailsMap = toImageDetailsMap(
    metadata.image_details_after || [],
  );

  const toProcessedImage = (
    image: TInputImage,
    imageMap: Map<
      number,
      {
        imageDimensions: { width: number; height: number };
        cropCoords: {
          cropX: number;
          cropY: number;
          cropWidth: number;
          cropHeight: number;
        };
        censorship: TSideBySideCensorship;
      }
    >,
  ): TSideBySideProcessedImage => {
    const imageDetails = imageMap.get(image.id_in_database);
    return {
      ...image,
      imageDimensions: imageDetails?.imageDimensions || { width: 1, height: 1 },
      cropCoords: imageDetails?.cropCoords || {
        cropX: 0,
        cropY: 0,
        cropWidth: 1,
        cropHeight: 1,
      },
      censorship: imageDetails?.censorship || {
        finalBboxesOnOriginal: {},
      },
    };
  };

  const allPairs = metadata.all_pairs.map((pair: any) => ({
    before: toProcessedImage(pair.before, beforeImageDetailsMap),
    after: toProcessedImage(pair.after, afterImageDetailsMap),
    concat: {
      put_url: pair.concat.url,
      s3_key: pair.concat.s3_key,
      thumbnail_s3_key: pair.concat.thumbnail_s3_key,
      filename: pair.concat.filename,
      id_in_database: pair.concat.id_in_database,
    },
  }));

  const unpairedBeforeImages = (metadata.unpaired_before_images || []).map(
    (image: TInputImage) => toProcessedImage(image, beforeImageDetailsMap),
  );
  const unpairedAfterImages = (metadata.unpaired_after_images || []).map(
    (image: TInputImage) => toProcessedImage(image, afterImageDetailsMap),
  );

  return {
    batchUuid: body.batch_uuid,
    allPairs,
    unpairedBeforeImages,
    unpairedAfterImages,
  };
};

export const assignPatientToSideBySideBatch = async (
  session: TSession,
  batchUuid: string,
  patientUuid: string,
): Promise<void> => {
  const clinicId = session?.clinicId || clinicosId;

  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${clinicId}/before-after/batch/${batchUuid}/assign-patient`,
    {
      method: "PATCH",
      body: JSON.stringify({ patient_uuid: patientUuid }),
    },
  );

  if (response.status === 204) return;

  const errorText = await response.text();
  throw new Error(
    `Failed to assign side-by-side batch ${batchUuid} to patient ${patientUuid}: HTTP ${response.status} - ${errorText}`,
  );
};

export type TGetBeforeAfterResponse = {
  batch_images: Array<{
    batch_uuid: string;
    patient_uuid?: string; // Optional field
    date_created: string;
    upload_types: ("RAW_BEFORE" | "RAW_AFTER" | "CONCAT")[];
    urls: string[];
    thumbnail_urls: string[];
  }>;
  page_count: number;
};

export type TAutoGenerateBeforeAfterRequest = {
  patient_uuid?: string | null;
  limit?: number;
};

export type TAutoGenerateBeforeAfterResponse = {
  pairs_generated: number;
  patients_processed: number;
  errors: string[] | null;
};

export const autoGenerateBeforeAfter = async (
  session: TSession,
  body: TAutoGenerateBeforeAfterRequest = {},
): Promise<TAutoGenerateBeforeAfterResponse> => {
  const clinicId = session?.clinicId || clinicosId;

  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${clinicId}/auto-generate-before-after`,
    {
      method: "POST",
      body: JSON.stringify({
        patient_uuid: body.patient_uuid ?? null,
        limit: body.limit ?? 100,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  const data = await response.json();
  return data;
};
export const getBeforeAfter = async (
  session: TSession,
  options?: {
    is_uploaded?: boolean;
    upload_types?: string[];
    limit?: number;
    offset?: number;
    sort_order?: "asc" | "desc";
    batch_uuid?: string;
    patient_uuid?: string;
  },
): Promise<TGetBeforeAfterResponse> => {
  const clinicId = session?.clinicId || clinicosId;

  const queryParams = new URLSearchParams();
  if (options?.is_uploaded !== undefined) {
    queryParams.append("is_uploaded", String(options.is_uploaded));
  }
  if (options?.upload_types && options.upload_types.length > 0) {
    queryParams.append("upload_types", options.upload_types.join(","));
  }
  if (options?.limit !== undefined) {
    queryParams.append("limit", String(options.limit));
  }
  if (options?.offset !== undefined) {
    queryParams.append("offset", String(options.offset));
  }
  if (options?.sort_order) {
    queryParams.append("sort_order", options.sort_order);
  }
  if (options?.batch_uuid) {
    queryParams.append("batch_uuid", options.batch_uuid);
  }
  if (options?.patient_uuid) {
    queryParams.append("patient_uuid", options.patient_uuid);
  }

  const queryString = queryParams.toString();
  const url = `${urlPrefixSideBySide}/${clinicId}/before-after${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await fetchWithAuthRetry(url, {
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  const data = await response.json();
  return data;
};

export const deleteProgressPhoto = async (
  session: TSession,
  s3_key: string,
) => {
  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/progress-photo?s3_key=${s3_key}`,
    {
      method: "DELETE",
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }
};

export type TGetTemplateResponse = {
  public_templates: {
    id: number;
    clinic_uuid: string;
    display_name: string;
    name: string;
    processing_type: TBeforeAfterProcessing;
    s3_key: string;
    is_public: boolean;
    created_at: string;
    updated_at: string;
    is_default_clinic_template: boolean;
    template: TTemplate;
  }[];
  private_templates: {
    id: number;
    clinic_uuid: string;
    display_name: string;
    name: string;
    processing_type: TBeforeAfterProcessing;
    s3_key: string;
    is_public: boolean;
    created_at: string;
    updated_at: string;
    is_default_clinic_template: boolean;
    template: TTemplate;
  }[];
};
export const getTemplates = async (
  session: TSession,
): Promise<TGetTemplateResponse> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/template`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  const templatesResult = await response.json();

  const publicTemplateWithContent = await Promise.all(
    templatesResult.public_templates.map(
      async (public_template: { name: string }) => {
        const templateContent = await getTemplateContent(session, {
          template_name: public_template.name,
          isPublic: true,
        });

        return {
          ...public_template,
          template: templateContent.content,
        };
      },
    ),
  );

  const privateTemplateWithContent = await Promise.all(
    templatesResult.private_templates.map(
      async (private_template: { name: string }) => {
        const templateContent = await getTemplateContent(session, {
          template_name: private_template.name,
          isPublic: false,
        });

        return {
          ...private_template,
          template: templateContent.content,
        };
      },
    ),
  );

  return {
    public_templates: publicTemplateWithContent,
    private_templates: privateTemplateWithContent,
  };
};

export type TPostTemplateResponse = {
  template: TTemplate;
  template_name: string;
};
export type TPostTemplateResquest = {
  template: TTemplate;
  display_name: string;
  processing_type: TBeforeAfterProcessing;
  is_public: boolean;
};
export const postTemplates = async (
  session: TSession,
  body: TPostTemplateResquest,
): Promise<TPostTemplateResponse> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/template`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  const responseJson: { presigned_get_url: string; template_name: string } =
    await response.json();
  const presignedUrlResponse = await fetch(responseJson.presigned_get_url, {
    method: "GET",
  });
  const result: TTemplate = await presignedUrlResponse.json();

  return { template: result, template_name: responseJson.template_name };
};

export type TGetTemplateContentQuery = {
  template_name: string;
  isPublic: boolean;
};
export type TGetTemplateContentResponse = {
  content: TTemplate;
};
export const getTemplateContent = async (
  session: TSession,
  query: TGetTemplateContentQuery,
): Promise<TGetTemplateContentResponse> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/template/${query.template_name}/content?is_public=${query.isPublic}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }
  const result = await response.json();
  return result;
};

export const postAssetsUpload = async (
  session: TSession,
  body: {
    imageFile: Blob;
  },
): Promise<{
  asset_id: number;
  put_url: string;
  get_url: string;
  s3_key: string;
}> => {
  const responseUpload = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/assets/upload`,
    {
      method: "POST",
    },
  );

  if (!responseUpload.ok) {
    throw new Error(`HTTP error ${responseUpload.status}`);
  }

  const resultUpload: {
    asset_id: number;
    put_url: string;
    get_url: string;
    s3_key: string;
  } = await responseUpload.json();

  const putResponse = await fetch(resultUpload.put_url, {
    method: "PUT",
    headers: {
      "Content-Type": "image/png",
    },
    body: body.imageFile,
  });

  if (!putResponse.ok) {
    throw new Error(`HTTP error ${putResponse.status}`);
  }

  const uploadCompleteResponse = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/assets/${resultUpload.asset_id}/upload-complete`,
    {
      method: "PATCH",
    },
  );
  if (!uploadCompleteResponse.ok) {
    throw new Error(`HTTP error ${uploadCompleteResponse.status}`);
  }
  return resultUpload;
};

export const getAssetsByIds = async (
  session: TSession,
  assetIds: number[],
): Promise<{
  assets: { asset_id: number; s3_key: string; get_url: string }[];
}> => {
  const queryParams = assetIds.map((id) => `asset_ids=${id}`).join("&");

  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/assets?is_uploaded=true&${queryParams}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch assets: ${response.status}`);
  }

  return await response.json();
};

export type TClinicAsset = {
  asset_id: number;
  s3_key: string;
  get_url: string;
};

export const getUploadedAssets = async (
  session: TSession,
): Promise<{
  assets: TClinicAsset[];
}> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/assets?is_uploaded=true`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch uploaded assets: ${response.status}`);
  }

  return await response.json();
};

export const deleteAsset = async (
  session: TSession,
  assetId: number,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/assets/${assetId}`,
    {
      method: "DELETE",
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to delete asset: ${response.status}`);
  }
};

export const patchTemplates = async (
  session: TSession,
  query: {
    template_name: string;
  },
  body: {
    display_name?: string;
    new_template?: TTemplate;
    is_public?: boolean;
    is_default_clinic_template?: boolean;
  },
): Promise<{
  template: {
    id: number;
    clinic_uuid: string;
    display_name: string;
    name: string;
    processing_type: TBeforeAfterProcessing;
    s3_key: string;
    is_default_clinic_template: boolean;
    created_at: string;
    updated_at: string;
  };
}> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/template/${query.template_name}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  return await response.json();
};

export type TGetConversationsResponse = {
  id: number;
  active: boolean;
  session_id: string;
  clinic_uuid: string;
  patient_uuid: string;
  patient_name: string;
  last_message: string;
  clinic_read: boolean;
  patient_read: boolean;
  clinic_user_id: string;
  last_message_at: string;
  conversation_type: TConversation;
}[];
export const getConversations = async (
  session: TSession,
): Promise<TGetConversationsResponse> => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/conversations`,
      {
        method: "GET",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    return data.conversations;
  } catch (error) {
    console.error("Error getting conversations:", error);
    throw error;
  }
};

export const patchConversations = async (
  session: TSession,
  query: {
    patient_uuid: string;
  },
  body: {
    active?: boolean;
    clinic_read?: boolean;
    patient_read?: boolean;
    conversation_type?: TConversation;
  },
) => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/patients/${query.patient_uuid}/conversations`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
  } catch (error) {
    console.error("Error updating conversation:", error);
    throw error;
  }
};

export type TGetMessagesResponse = {
  messages: {
    id: number;
    message: string;
    created_at: string;
    ai_response: string;
    image_urls: string[];
    thumbnail_urls: string[];
    sender_type: TMessageSender;
  }[];
  patient_info: {
    dob: string;
    sex: string;
    uuid: string;
    email: string;
    phone: string;
    location: string;
    last_name: string;
    first_name: string;
    created_at: string;
    updated_at: string;
    upload_type: string;
    invite_code: string;
    areas_of_interest: string[];
    invite_code_expires_at: string;
    procedures_of_interest: string[];
  };
  patient_info_image_urls: string[];
  patient_info_thumbnail_urls: string[];
};
export const getMessages = async (
  session: TSession,
  query: {
    page?: number;
    per_page?: number;
    patient_uuid: string;
    get_patient_info?: boolean;
    sender_type?: TMessageSender;
  },
): Promise<TGetMessagesResponse> => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/patients/${query.patient_uuid}/messages?get_patient_info=${query.get_patient_info}`,
      {
        method: "GET",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error getting messages:", error);
    throw error;
  }
};

export const getLastMessage = async (
  session: TSession,
  query: {
    patient_uuid: string;
    conversation_id: number;
  },
): Promise<{
  last_message_id: number;
}> => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/clinic/${session.clinicId}/patients/${query.patient_uuid}/conversations/${query.conversation_id}/last-message-id`,
      {
        method: "GET",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error getting last message:", error);
    throw error;
  }
};

export const postMessages = async (
  session: TSession,
  query: {
    patient_uuid: string;
  },
  body: {
    message: string;
    sender_type: string;
  },
) => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/patients/${query.patient_uuid}/lead-messages`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
  } catch (error) {
    console.error("Error posting message:", error);
    throw error;
  }
};

export type TCreateManualPairsRequestPair = {
  before_image_id: number;
  after_image_id: number;
};

export type TCreatedManualPair = {
  before_image_id: number;
  after_image_id: number;
  concat_image_id: number;
  concat_s3_key: string;
  concat_put_url: string;
  concat_thumbnail_s3_key: string;
  concat_thumbnail_put_url: string;
};

export type TCreateManualPairsResponse = {
  pairs: TCreatedManualPair[];
};

export const createSideBySideManualPairs = async (
  session: TSession,
  batchUuid: string,
  pairs: TCreateManualPairsRequestPair[],
): Promise<TCreateManualPairsResponse> => {
  const clinicId = session?.clinicId || clinicosId;

  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${clinicId}/before-after/batch/${batchUuid}/add-manual-pairs-to-batch`,
    {
      method: "POST",
      body: JSON.stringify({ pairs }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to create manual pairs for batch ${batchUuid}: HTTP ${response.status} - ${errorText}`,
    );
  }

  return await response.json();
};

export const markSideBySideImagesUploaded = async (
  session: TSession,
  imageIds: number[],
): Promise<void> => {
  if (imageIds.length === 0) return;
  const clinicId = session?.clinicId || clinicosId;

  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${clinicId}/images/mark-uploaded`,
    {
      method: "POST",
      body: JSON.stringify({ image_ids: imageIds }),
    },
  );

  if (response.status === 204) return;

  const errorText = await response.text();
  throw new Error(
    `Failed to mark side-by-side images as uploaded: HTTP ${response.status} - ${errorText}`,
  );
};

export type TGetSideBySideImagePutUrlsResponse = {
  put_urls: string[];
  thumbnail_put_urls: string[];
};

export const getSideBySideImagePutUrls = async (
  session: TSession,
  imageIds: number[],
): Promise<TGetSideBySideImagePutUrlsResponse> => {
  const queryString = imageIds.map((id) => `image_ids=${id}`).join("&");

  const response = await fetchWithAuthRetry(
    `${urlPrefixSideBySide}/${session.clinicId}/images/put-urls?${queryString}`,
    { method: "GET" },
  );

  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`);
  }

  return await response.json();
};

export type TCreateDummyDataRequest = {
  create_patients: boolean; // Flag to create dummy patients
  create_appointments: boolean; // Flag to create dummy appointments
};

export type TCreateDummyDataResponse = {
  patients_created: number; // Number of dummy patients created
  appointments_created: number; // Number of dummy appointments created
  message: string; // Message indicating success or failure
};

export const createDummyData = async (
  session: TSession,
  clinicId: string,
  requestBody: TCreateDummyDataRequest,
): Promise<TCreateDummyDataResponse> => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${clinicId}/dummy-data`, // Endpoint for creating dummy data
      {
        method: "POST",
        body: JSON.stringify(requestBody),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data: TCreateDummyDataResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating dummy data", error);
    throw error;
  }
};

export type TDeleteDummyAppointmentsResponse = {
  deleted_count: number; // Number of appointments deleted
  message: string; // Message indicating success
};

export const deleteDummyAppointments = async (
  session: TSession,
  clinicId: string,
  cutoffDaysAgo?: number,
): Promise<TDeleteDummyAppointmentsResponse> => {
  try {
    // Build URL with optional query parameter
    let url = `${urlPrefix}/${clinicId}/dummy-data/appointments`;
    if (cutoffDaysAgo !== undefined) {
      url += `?cutoff_days_ago=${cutoffDaysAgo}`;
    }

    const response = await fetchWithAuthRetry(url, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data: TDeleteDummyAppointmentsResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error deleting dummy appointments", error);
    throw error;
  }
};
